import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Button, Divider, Tab } from "@mui/material";
import qs from "qs";
import React, { useCallback, useMemo, useState, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import type { WorkflowSignalType } from "@entrilia/shared/api/workflowTypes";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import NoAccessPlaceholder from "@entrilia/shared/components/NoAccessPlaceholder";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import useTabSearchParams from "@entrilia/shared/hooks/useTabSearchParams";
import useToggleState from "@entrilia/shared/hooks/useToggleState";
import { logError } from "@entrilia/shared/logging";
import { distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../api/adminApi";
import type { ObjectFieldValuesDetails } from "../../api/types/objectTypes";
import useClientContext from "../../context/useClientContext";
import useGetObjectPageLayout from "../../hooks/useGetObjectPageLayout";
import useObjectChangePolling from "../../hooks/useObjectChangePolling";
import ObjectWorkflowActions from "../approvals/workflows/ObjectWorkflowActions";
import GeneralPageHeader from "../common/GeneralPageHeader";
import ObjectTitle from "../common/ObjectTitle";
import FullHeightFlexPage from "../common/layout/FullHeightFlexPage";
import StretchedScrollableContent from "../common/layout/StretchedScrollableContent";
import DeleteObjectDialog from "./DeleteObjectDialog";
import ObjectMainInfo from "./ObjectMainInfo";
import ContactAccessMatrix from "./access-matrix/ContactAccessMatrix";
import InternalUserAccessMatrix from "./access-matrix/InternalUserAccessMatrix";
import { getDataCatalogueEntityType } from "./entityTypesHelper";
import EntityCardFiles from "./files/EntityCardFiles";
import type { ObjectLayoutSection } from "./helpers";
import { ObjectCardChecksContextProvider } from "./ObjectCardChecksContext";
import type { ObjectCardPreviewConfig } from "./objectCardPreviewState";
import { withSource, type ObjectCardSource } from "./objectCardSource";
import useObjectCardChecks from "./useObjectCardChecks";

interface Props {
  objectId: string;
  objectType: string;
  backButtonPath: string;
  showLogoSection?: boolean;
  forApproval?: boolean;
  cardPreview?: ObjectCardPreviewConfig;
  source?: ObjectCardSource;
  isSourceLoading?: boolean;
  onRefresh?: () => Promise<void>;
  renderTopSection?: (objectData: ObjectFieldValuesDetails) => ReactNode;
}

const tabValues = ["main", "files", "portfoliocontacts", "internalusers"] as const;
type TabValue = (typeof tabValues)[number];

const ObjectDetailsPage = ({
  objectId,
  objectType,
  backButtonPath,
  showLogoSection,
  forApproval,
  cardPreview,
  source,
  isSourceLoading,
  onRefresh,
  renderTopSection,
}: Props) => {
  const { hasAnyPermission, isPortalFeatureEnabled, portfolioMonitoringApp, updateMenuBadges } = useClientContext();
  const navigate = useNavigate();
  const location = useLocation();

  const fullBackButtonPath = useMemo(() => {
    const tab = (location.state as { tab?: string })?.tab;
    if (!tab) {
      return backButtonPath;
    }

    const [basePath, existingQuery = ""] = backButtonPath.split("?");
    const query = existingQuery ? { ...qs.parse(existingQuery), tab } : { tab };
    return `${basePath}?${qs.stringify(query)}`;
  }, [location.state, backButtonPath]);

  const getObjectDefinition = useCallback(() => adminApi.getObjectDefinition(objectType), [objectType]);
  const [objectDefinitionWithPermissions, fetchDefinitionError] = useFetch(getObjectDefinition);

  const objectClassDefinition = useMemo(() => {
    const definition = objectDefinitionWithPermissions?.objectClassDefinition;
    return definition && source ? withSource(definition, source) : definition;
  }, [objectDefinitionWithPermissions, source]);

  const getObjectData = useCallback(() => adminApi.getObjectById(objectType, objectId), [objectId, objectType]);
  const [objectData, fetchDetailsError, { setData: setObjectData, fetch: fetchObjectData }] = useFetch(getObjectData);

  const getCardLayout = useGetObjectPageLayout(objectType);

  const [cardLayout, fetchCardLayoutError] = useFetch(getCardLayout);

  const [objectAccessCategories, fetchAccessCategoriesError] = useFetch(adminApi.getObjectAccessCategories);

  const cardChecks = useObjectCardChecks({
    objectType,
    objectId,
    objectData,
    objectDefinition: objectClassDefinition,
    cardPreview,
  });

  usePageTitle(objectDefinitionWithPermissions?.objectClassDefinition?.objectName || "");

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);

  const [renderKey, toggleRenderKey] = useToggleState(false);

  const { selectedTab, onTabChange } = useTabSearchParams(tabValues);

  const { setPreviewChecks, refetchChecks } = cardChecks;
  const refreshObjectValues = useCallback(async () => {
    refetchChecks();
    await Promise.all([onRefresh?.(), fetchObjectData()]);
    setPreviewChecks(undefined);
    setHasUnsavedEdits(false);
    toggleRenderKey();
  }, [fetchObjectData, onRefresh, refetchChecks, setPreviewChecks, toggleRenderKey]);

  const { forcePoll } = useObjectChangePolling({
    objectType,
    objectId,
    pendingUpdate: objectData?.pendingUpdate ?? false,
    onChange: refreshObjectValues,
  });

  const displayedSections = useMemo<ObjectLayoutSection[]>(() => {
    if (objectDefinitionWithPermissions === undefined) {
      return [];
    }

    const result: ObjectLayoutSection[] = [];

    const {
      objectClassDefinition: { supportedFeatures },
      objectPermissions,
    } = objectDefinitionWithPermissions;

    if (hasAnyPermission(objectPermissions.notesReadPermissions) && supportedFeatures.includes("Notes")) {
      result.push("notes");
    }

    if (
      (forApproval || hasAnyPermission(objectPermissions.filesReadPermissions)) &&
      supportedFeatures.includes("CardFilePreview")
    ) {
      result.push("file_preview");
    }

    if (supportedFeatures.includes("Checklists")) {
      result.push("tasks");
    }

    if (showLogoSection) {
      result.push("logo");
    }

    return result;
  }, [forApproval, hasAnyPermission, objectDefinitionWithPermissions, showLogoSection]);

  const fetchError = fetchDetailsError || fetchDefinitionError || fetchAccessCategoriesError || fetchCardLayoutError;

  if (fetchError) {
    if (fetchDetailsError?.type === "NotFound") {
      return <Navigate to={fullBackButtonPath} />;
    }

    if (fetchError.code === 403) {
      return <NoAccessPlaceholder text="You do not have permission to view this object." />;
    }

    logError(fetchError, "[ObjectDetails]");
    return <DataLoadingFailed />;
  }

  if (
    !objectData ||
    !objectDefinitionWithPermissions ||
    !objectClassDefinition ||
    !objectAccessCategories ||
    !cardLayout ||
    isSourceLoading
  ) {
    return <InlineLoader />;
  }

  const { objectPermissions } = objectDefinitionWithPermissions;

  const handleDeleted = () => {
    setTimeout(() => navigate(fullBackButtonPath));
  };

  const handleWorkflowActionExecuted = (signalType: WorkflowSignalType) => {
    updateMenuBadges();

    if (signalType === "DeleteInvoice") {
      handleDeleted();
    } else {
      refreshObjectValues();
    }
  };

  const handleEntityUpdate = (newObjectData: ObjectFieldValuesDetails) => {
    setObjectData(newObjectData);
    refetchChecks();
  };

  const handleApprovalActionExecuted = () => {
    updateMenuBadges();
    refreshObjectValues();
  };

  const hasPermissionsToManageObjectData = hasAnyPermission(objectPermissions.objectDataWritePermissions);

  const hasPermissionsToManageObjectContacts = hasAnyPermission(objectPermissions.objectContactsWritePermissions);

  const hasPermissionsToManageObjectInternalUsers = hasAnyPermission(
    objectPermissions.objectInternalUsersWritePermissions
  );

  const areFilesSupported =
    hasAnyPermission(objectPermissions.filesReadPermissions) &&
    objectClassDefinition.supportedFeatures.includes("Files");

  const hasPermissionsToManageFiles = hasAnyPermission(objectPermissions.filesWritePermissions);

  const contactAccessCategories =
    objectClassDefinition.objectContactCategoryType !== undefined
      ? objectAccessCategories.filter((c) => c.type === objectClassDefinition.objectContactCategoryType)
      : [];

  const internalUserAccessCategories =
    objectClassDefinition.internalUserPermissionsCategoryType !== undefined
      ? objectAccessCategories.filter((c) => c.type === objectClassDefinition.internalUserPermissionsCategoryType)
      : [];

  const isContactAccessMatrixSupported =
    hasAnyPermission(objectPermissions.objectContactsReadPermissions) &&
    objectClassDefinition.supportedContactRoles.length > 0 &&
    contactAccessCategories.length > 0;

  const isInternalUserAccessMatrixSupported =
    hasAnyPermission(objectPermissions.objectInternalUsersReadPermissions) &&
    objectClassDefinition.supportedFeatures.includes("InternalUserPermissions") &&
    objectClassDefinition.supportedInternalUserRoles.length > 0 &&
    internalUserAccessCategories.length > 0;

  const hasPermissionsToManageNotes = hasAnyPermission(objectPermissions.notesWritePermissions);

  const isCustomSourceObject = objectClassDefinition.source === "Custom";

  const dataCatalogEntityType = getDataCatalogueEntityType(objectClassDefinition.objectType);

  const workflowsWithAvailableActions = distinctBy(
    objectData.workflowMetadata.filter(
      (workflow) => workflow.availableUserSignals.length > 0 && !workflow.failureMessage
    ),
    (w) => w.workflowType
  );

  return (
    <ObjectCardChecksContextProvider actionChecks={cardChecks.actionChecks} duplicates={cardChecks.duplicates}>
      <TabContext value={selectedTab}>
        <FullHeightFlexPage>
          <Box>
            <GeneralPageHeader
              TitleComponent={
                <ObjectTitle
                  title={objectData.displayValues.primaryValue || objectData.name}
                  secondaryTitle={objectData.displayValues.secondaryValue}
                  typeName={objectClassDefinition.objectName}
                  logoUrl={objectData.logoUrl}
                />
              }
              hideBorderLine
              showDefaultBackButtonTo={fullBackButtonPath}
            >
              {hasPermissionsToManageObjectData && workflowsWithAvailableActions.length > 0 && (
                <>
                  {workflowsWithAvailableActions.map((workflow, index) => (
                    <React.Fragment key={workflow.workflowId}>
                      <ObjectWorkflowActions
                        objectTypeName={objectClassDefinition.objectName}
                        objectName={objectData.displayValues.primaryValue || objectData.name}
                        objectType={objectType}
                        objectId={objectId}
                        workflowMetadata={workflow}
                        actionsWithChecks={objectClassDefinition.workflowActionsWithChecks}
                        hasUnsavedEdits={hasUnsavedEdits}
                        onActionExecuted={handleWorkflowActionExecuted}
                      />
                      {index < workflowsWithAvailableActions.length - 1 && <Divider flexItem orientation="vertical" />}
                    </React.Fragment>
                  ))}
                </>
              )}

              {isCustomSourceObject &&
                hasPermissionsToManageObjectData &&
                isPortalFeatureEnabled("CustomObjectsEnabled") && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    Delete
                  </Button>
                )}
            </GeneralPageHeader>

            <TabList
              onChange={(_, tab: TabValue) => {
                setHasUnsavedEdits(false);
                onTabChange(tab);
              }}
            >
              <Tab value="main" label="Main Info" sx={{ ml: 3 }} />
              {areFilesSupported && isPortalFeatureEnabled("EntityFilesEnabled") && <Tab value="files" label="Files" />}
              {isContactAccessMatrixSupported && portfolioMonitoringApp?.dataCollection?.enabled && (
                <Tab value="portfoliocontacts" label="Portfolio Contacts" />
              )}
              {isInternalUserAccessMatrixSupported && portfolioMonitoringApp?.dataCollection?.enabled && (
                <Tab value="internalusers" label="Internal Users" />
              )}
            </TabList>
          </Box>

          <StretchedScrollableContent>
            <TabPanel
              value="main"
              sx={{ height: "100%", m: 0, px: 3, pt: 2.5, pb: 0, backgroundColor: "background.grey" }}
            >
              <ObjectMainInfo
                key={renderKey.toString()}
                objectData={objectData}
                objectDefinition={objectClassDefinition}
                cardLayout={cardLayout}
                dataCatalogEntityType={dataCatalogEntityType}
                hasPermissionsToManageObjectData={hasPermissionsToManageObjectData}
                hasPermissionsToManageFiles={hasPermissionsToManageFiles}
                hasPermissionsToManageNotes={hasPermissionsToManageNotes}
                displayedSections={displayedSections}
                cardPreview={cardPreview}
                source={source}
                renderTopSection={renderTopSection}
                onPreviewChecksChange={setPreviewChecks}
                onEntityUpdate={handleEntityUpdate}
                onApprovalActionExecuted={handleApprovalActionExecuted}
                onDocumentDeleted={forcePoll}
                onUnsavedEditsChange={setHasUnsavedEdits}
              />
            </TabPanel>

            {areFilesSupported && isPortalFeatureEnabled("EntityFilesEnabled") && (
              <TabPanel value="files" sx={{ height: "100%", m: 0, px: 0, py: 0 }}>
                <EntityCardFiles
                  entityType={dataCatalogEntityType}
                  entityId={objectData.id}
                  entityName={objectData.name}
                  objectType={objectData.objectType}
                  hasPermissionsToManage={hasPermissionsToManageFiles}
                />
              </TabPanel>
            )}

            {isContactAccessMatrixSupported && portfolioMonitoringApp?.dataCollection?.enabled && (
              <TabPanel value="portfoliocontacts" sx={{ height: "100%", m: 0, px: 3, pt: 2.5 }}>
                <ContactAccessMatrix
                  objectId={objectId}
                  objectDefinition={objectClassDefinition}
                  categories={contactAccessCategories}
                  hasPermissionsToManage={hasPermissionsToManageObjectContacts}
                />
              </TabPanel>
            )}

            {isInternalUserAccessMatrixSupported && portfolioMonitoringApp?.dataCollection?.enabled && (
              <TabPanel value="internalusers" sx={{ height: "100%", m: 0, px: 3, pt: 2.5 }}>
                <InternalUserAccessMatrix
                  objectId={objectId}
                  objectDefinition={objectClassDefinition}
                  categories={internalUserAccessCategories}
                  hasPermissionsToManage={hasPermissionsToManageObjectInternalUsers}
                />
              </TabPanel>
            )}
          </StretchedScrollableContent>
        </FullHeightFlexPage>

        <DeleteObjectDialog
          objectId={objectId}
          objectType={objectType}
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onDeleted={handleDeleted}
        />
      </TabContext>
    </ObjectCardChecksContextProvider>
  );
};

export default ObjectDetailsPage;
